import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';
import { useResponsive } from '../hooks/useResponsive';
import useMediaUpload from '../hooks/useMediaUpload';
import MediaUploadField from './MediaUploadField';
import { extractThumbnailFromVideo, getVideoDuration } from '../utils/videoThumbnail';

const DrillLibraryManager = ({ profile }) => {
  const { isDesktop } = useResponsive();
  const [drills, setDrills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingDrill, setEditingDrill] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [difficultyFilter, setDifficultyFilter] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: 'hitting',
    difficulty_level: 'intermediate',
    video_url: '',
    thumbnail_url: '',
    equipment_needed: [],
    duration_minutes: null,
  });

  // Media upload state
  const [videoSource, setVideoSource] = useState('url'); // 'url' or 'upload'
  const [videoFile, setVideoFile] = useState(null);
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [videoStoragePath, setVideoStoragePath] = useState(null);
  const [thumbnailStoragePath, setThumbnailStoragePath] = useState(null);
  const [extractingThumbnail, setExtractingThumbnail] = useState(false);

  // Media upload hook
  const {
    uploadFile,
    deleteFile,
    validateFile,
    generateFilename,
    uploading: mediaUploading,
    error: mediaError,
  } = useMediaUpload();

  const categories = [
    'hitting',
    'pitching',
    'fielding',
    'baserunning',
    'conditioning',
    'mental',
    'other',
  ];

  const difficulties = ['beginner', 'intermediate', 'advanced'];

  useEffect(() => {
    loadDrills();
  }, []);

  const loadDrills = async () => {
    try {
      setLoading(true);
      setError('');

      const { data, error } = await supabase.from('drills').select('*').order('name');

      if (error) throw error;
      setDrills(data || []);
    } catch (err) {
      console.error('Error loading drills:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setShowForm(true);
    setEditingDrill(null);
    setFormData({
      name: '',
      description: '',
      category: 'hitting',
      difficulty_level: 'intermediate',
      video_url: '',
      thumbnail_url: '',
      equipment_needed: [],
      duration_minutes: null,
    });
    // Reset media upload state
    setVideoSource('url');
    setVideoFile(null);
    setThumbnailFile(null);
    setVideoStoragePath(null);
    setThumbnailStoragePath(null);
    setExtractingThumbnail(false);
    setError('');
    setSuccess('');
  };

  const handleEdit = (drill) => {
    setShowForm(true);
    setEditingDrill(drill.id);
    setFormData({
      name: drill.name || '',
      description: drill.description || '',
      category: drill.category || 'hitting',
      difficulty_level: drill.difficulty_level || 'intermediate',
      video_url: drill.video_url || '',
      thumbnail_url: drill.thumbnail_url || '',
      equipment_needed: drill.equipment_needed || [],
      duration_minutes: drill.duration_minutes || null,
    });
    // Set media source state based on existing drill data
    setVideoSource(drill.video_source || 'url');
    setVideoFile(null);
    setThumbnailFile(null);
    setVideoStoragePath(drill.video_storage_path || null);
    setThumbnailStoragePath(drill.thumbnail_storage_path || null);
    setExtractingThumbnail(false);
    setError('');
    setSuccess('');
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingDrill(null);
    setFormData({
      name: '',
      description: '',
      category: 'hitting',
      difficulty_level: 'intermediate',
      video_url: '',
      thumbnail_url: '',
      equipment_needed: [],
      duration_minutes: null,
    });
    // Reset media upload state
    setVideoSource('url');
    setVideoFile(null);
    setThumbnailFile(null);
    setVideoStoragePath(null);
    setThumbnailStoragePath(null);
    setExtractingThumbnail(false);
    setError('');
    setSuccess('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      let uploadedVideoPath = videoStoragePath;
      let uploadedThumbnailPath = thumbnailStoragePath;

      // Upload video file if selected
      if (videoSource === 'upload' && videoFile) {
        const orgId = profile?.organization_id || 'global';
        const drillId = editingDrill || 'temp-' + Date.now();
        const extension = videoFile.name.split('.').pop();
        const filename = generateFilename(orgId, drillId, extension, 'video');

        const result = await uploadFile(videoFile, 'drill-videos', filename);

        if (!result.success) {
          throw new Error(`Video upload failed: ${result.error}`);
        }

        uploadedVideoPath = result.path;

        // Delete old video if updating and had previous upload
        if (editingDrill && videoStoragePath && videoStoragePath !== uploadedVideoPath) {
          await deleteFile('drill-videos', videoStoragePath);
        }
      }

      // Upload thumbnail file if it was extracted from video
      if (videoSource === 'upload' && thumbnailFile) {
        const orgId = profile?.organization_id || 'global';
        const drillId = editingDrill || 'temp-' + Date.now();
        const extension = thumbnailFile.name.split('.').pop();
        const filename = generateFilename(orgId, drillId, extension, 'thumbnail');

        const result = await uploadFile(thumbnailFile, 'drill-thumbnails', filename);

        if (!result.success) {
          throw new Error(`Thumbnail upload failed: ${result.error}`);
        }

        uploadedThumbnailPath = result.path;

        // Delete old thumbnail if updating and had previous upload
        if (
          editingDrill &&
          thumbnailStoragePath &&
          thumbnailStoragePath !== uploadedThumbnailPath
        ) {
          await deleteFile('drill-thumbnails', thumbnailStoragePath);
        }
      }

      // Prepare drill data with media sources
      const drillData = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        category: formData.category,
        difficulty_level: formData.difficulty_level,
        equipment_needed: formData.equipment_needed.filter((e) => e.trim()),
        duration_minutes: formData.duration_minutes || null,
        organization_id: profile?.organization_id || null,
        created_by: profile?.id,
        // Video fields
        video_source: videoSource,
        video_url: videoSource === 'url' ? formData.video_url.trim() || null : null,
        video_storage_path: videoSource === 'upload' ? uploadedVideoPath : null,
        video_file_size: videoSource === 'upload' && videoFile ? videoFile.size : null,
        // Thumbnail fields - auto-extracted from video or null for URL videos
        thumbnail_source: videoSource === 'upload' && uploadedThumbnailPath ? 'video_frame' : null,
        thumbnail_url: null, // We don't use thumbnail URLs anymore
        thumbnail_storage_path: videoSource === 'upload' ? uploadedThumbnailPath : null,
      };

      if (editingDrill) {
        // Update existing drill
        const { error } = await supabase.from('drills').update(drillData).eq('id', editingDrill);

        if (error) throw error;
        setSuccess('Drill updated successfully!');
      } else {
        // Create new drill
        const { error } = await supabase.from('drills').insert(drillData);

        if (error) throw error;
        setSuccess('Drill created successfully!');
      }

      await loadDrills();
      handleCancel();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error saving drill:', err);
      setError(err.message);
    }
  };

  const handleDelete = async (drill) => {
    if (
      !window.confirm(`Are you sure you want to delete "${drill.name}"? This cannot be undone.`)
    ) {
      return;
    }

    try {
      // Check if drill has any assignments
      const { data: assignments, error: checkError } = await supabase
        .from('assigned_drills')
        .select('id')
        .eq('drill_id', drill.id)
        .limit(1);

      if (checkError) throw checkError;

      if (assignments && assignments.length > 0) {
        setError('Cannot delete drill: it has been assigned to players. Please unassign it first.');
        setTimeout(() => setError(''), 5000);
        return;
      }

      // Delete drill from database
      const { error } = await supabase.from('drills').delete().eq('id', drill.id);

      if (error) throw error;

      // Clean up storage files
      if (drill.video_storage_path) {
        await deleteFile('drill-videos', drill.video_storage_path);
      }
      if (drill.thumbnail_storage_path) {
        await deleteFile('drill-thumbnails', drill.thumbnail_storage_path);
      }

      setSuccess('Drill deleted successfully!');
      await loadDrills();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error deleting drill:', err);
      setError(err.message);
    }
  };

  const handleEquipmentChange = (index, value) => {
    const newEquipment = [...formData.equipment_needed];
    newEquipment[index] = value;
    setFormData({ ...formData, equipment_needed: newEquipment });
  };

  const addEquipmentField = () => {
    setFormData({
      ...formData,
      equipment_needed: [...formData.equipment_needed, ''],
    });
  };

  const removeEquipmentField = (index) => {
    const newEquipment = formData.equipment_needed.filter((_, i) => i !== index);
    setFormData({ ...formData, equipment_needed: newEquipment });
  };

  /**
   * Handle video file upload - automatically extracts thumbnail
   */
  const handleVideoUpload = async (file) => {
    try {
      setVideoFile(file);
      setExtractingThumbnail(true);

      // Extract thumbnail from video (at 1 second mark)
      const thumbnail = await extractThumbnailFromVideo(file, 1);
      setThumbnailFile(thumbnail);
      setExtractingThumbnail(false);

      return { success: true };
    } catch (error) {
      console.error('Error extracting thumbnail:', error);
      setExtractingThumbnail(false);
      // Don't fail the upload if thumbnail extraction fails
      return { success: true, warning: 'Video uploaded but thumbnail extraction failed' };
    }
  };

  /**
   * Handle video file removal
   */
  const handleVideoRemove = async () => {
    setVideoFile(null);
    setThumbnailFile(null);
    setVideoStoragePath(null);
    setThumbnailStoragePath(null);
  };

  const filteredDrills = drills.filter((drill) => {
    const matchesSearch =
      !searchTerm ||
      drill.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (drill.description && drill.description.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCategory = !categoryFilter || drill.category === categoryFilter;
    const matchesDifficulty = !difficultyFilter || drill.difficulty_level === difficultyFilter;
    return matchesSearch && matchesCategory && matchesDifficulty;
  });

  const styles = {
    container: {
      width: '100%',
    },
    card: {
      backgroundColor: 'white',
      borderRadius: '8px',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      padding: isDesktop ? '30px' : '16px',
      marginBottom: isDesktop ? '20px' : '12px',
    },
    cardHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '20px',
      flexWrap: 'wrap',
      gap: '10px',
    },
    cardTitle: {
      fontSize: '20px',
      fontWeight: 'bold',
      color: '#333',
      margin: 0,
    },
    button: {
      padding: isDesktop ? '10px 20px' : '14px 20px',
      fontSize: '14px',
      fontWeight: '500',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      transition: 'background-color 0.2s',
      minHeight: '44px',
    },
    primaryButton: {
      backgroundColor: '#007bff',
    },
    successButton: {
      backgroundColor: '#28a745',
    },
    dangerButton: {
      backgroundColor: '#dc3545',
    },
    secondaryButton: {
      backgroundColor: '#6c757d',
    },
    filters: {
      display: 'flex',
      flexDirection: isDesktop ? 'row' : 'column',
      gap: '10px',
      marginBottom: '20px',
      flexWrap: 'wrap',
    },
    filterInput: {
      flex: isDesktop ? '1' : 'auto',
      minWidth: isDesktop ? '200px' : '100%',
      padding: '10px',
      fontSize: '14px',
      border: '1px solid #ddd',
      borderRadius: '4px',
      minHeight: '44px',
    },
    select: {
      flex: isDesktop ? '0 0 auto' : 'auto',
      minWidth: isDesktop ? '150px' : '100%',
      padding: '10px',
      fontSize: '14px',
      border: '1px solid #ddd',
      borderRadius: '4px',
      backgroundColor: 'white',
      minHeight: '44px',
    },
    tableContainer: {
      overflowX: 'auto',
      WebkitOverflowScrolling: 'touch',
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      minWidth: '800px',
    },
    th: {
      textAlign: 'left',
      padding: '12px',
      borderBottom: '2px solid #e0e0e0',
      fontWeight: '600',
      color: '#666',
      fontSize: '14px',
    },
    td: {
      padding: '12px',
      borderBottom: '1px solid #f0f0f0',
      fontSize: '14px',
    },
    badge: {
      display: 'inline-block',
      padding: '4px 12px',
      borderRadius: '12px',
      fontSize: '12px',
      fontWeight: '500',
      textTransform: 'capitalize',
    },
    actionButtons: {
      display: 'flex',
      gap: '8px',
    },
    smallButton: {
      padding: isDesktop ? '6px 12px' : '10px 16px',
      fontSize: isDesktop ? '12px' : '14px',
      fontWeight: '500',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      minHeight: '44px',
    },
    form: {
      display: 'grid',
      gridTemplateColumns: isDesktop ? '1fr 1fr' : '1fr',
      gap: '20px',
    },
    formGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
    },
    formGroupFull: {
      gridColumn: '1 / -1',
    },
    label: {
      fontSize: '14px',
      fontWeight: '500',
      color: '#333',
    },
    input: {
      padding: '10px',
      fontSize: '14px',
      border: '1px solid #ddd',
      borderRadius: '4px',
      minHeight: '44px',
    },
    textarea: {
      padding: '10px',
      fontSize: '14px',
      border: '1px solid #ddd',
      borderRadius: '4px',
      minHeight: '100px',
      fontFamily: 'inherit',
      resize: 'vertical',
    },
    formActions: {
      gridColumn: '1 / -1',
      display: 'flex',
      gap: '10px',
      justifyContent: 'flex-end',
      marginTop: '10px',
    },
    error: {
      padding: '12px',
      backgroundColor: '#fee',
      color: '#c33',
      borderRadius: '4px',
      fontSize: '14px',
      border: '1px solid #fcc',
      marginBottom: '20px',
    },
    success: {
      padding: '12px',
      backgroundColor: '#efe',
      color: '#3c3',
      borderRadius: '4px',
      fontSize: '14px',
      border: '1px solid #cfc',
      marginBottom: '20px',
    },
    drillCard: {
      backgroundColor: 'white',
      border: '1px solid #e0e0e0',
      borderRadius: '8px',
      padding: '16px',
      marginBottom: '12px',
    },
    drillCardHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: '12px',
    },
    drillCardTitle: {
      fontSize: '18px',
      fontWeight: 'bold',
      color: '#333',
      margin: '0 0 4px 0',
    },
    drillCardMeta: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '8px',
      marginBottom: '12px',
    },
    equipmentList: {
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
    },
    equipmentItem: {
      display: 'flex',
      gap: '8px',
      alignItems: 'center',
    },
    removeButton: {
      padding: '8px 12px',
      backgroundColor: '#dc3545',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '12px',
      minHeight: '40px',
    },
    addButton: {
      padding: '8px 12px',
      backgroundColor: '#28a745',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '14px',
      marginTop: '8px',
      minHeight: '44px',
    },
    sourceToggle: {
      display: 'flex',
      gap: '8px',
      marginBottom: '12px',
    },
    toggleButton: {
      flex: 1,
      padding: '10px',
      fontSize: '14px',
      fontWeight: '500',
      border: '1px solid #ddd',
      borderRadius: '4px',
      cursor: 'pointer',
      transition: 'all 0.2s',
      backgroundColor: 'white',
      color: '#666',
      minHeight: '44px',
    },
    toggleButtonActive: {
      backgroundColor: '#007bff',
      color: 'white',
      borderColor: '#007bff',
    },
    mediaSection: {
      padding: '16px',
      backgroundColor: '#f8f9fa',
      borderRadius: '4px',
      border: '1px solid #e0e0e0',
    },
    sectionLabel: {
      fontSize: '14px',
      fontWeight: '600',
      color: '#333',
      marginBottom: '12px',
      display: 'block',
    },
  };

  const getCategoryBadgeStyle = (category) => {
    const colors = {
      hitting: { bg: '#e8f5e9', color: '#2e7d32' },
      pitching: { bg: '#e3f2fd', color: '#1565c0' },
      fielding: { bg: '#fff3e0', color: '#e65100' },
      baserunning: { bg: '#f3e5f5', color: '#6a1b9a' },
      conditioning: { bg: '#fce4ec', color: '#c2185b' },
      mental: { bg: '#e0f2f1', color: '#00695c' },
      other: { bg: '#f5f5f5', color: '#616161' },
    };
    const style = colors[category] || colors.other;
    return {
      ...styles.badge,
      backgroundColor: style.bg,
      color: style.color,
    };
  };

  const getDifficultyBadgeStyle = (difficulty) => {
    const colors = {
      beginner: { bg: '#e8f5e9', color: '#2e7d32' },
      intermediate: { bg: '#fff3e0', color: '#e65100' },
      advanced: { bg: '#ffebee', color: '#c62828' },
    };
    const style = colors[difficulty] || colors.intermediate;
    return {
      ...styles.badge,
      backgroundColor: style.bg,
      color: style.color,
    };
  };

  if (loading) {
    return <div style={{ padding: '20px', textAlign: 'center' }}>Loading drills...</div>;
  }

  return (
    <div style={styles.container}>
      {error && <div style={styles.error}>{error}</div>}
      {success && <div style={styles.success}>{success}</div>}

      {showForm ? (
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <h2 style={styles.cardTitle}>{editingDrill ? 'Edit Drill' : 'Create New Drill'}</h2>
          </div>
          <form onSubmit={handleSubmit} style={styles.form}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Drill Name *</label>
              <input
                type="text"
                style={styles.input}
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Tee Work - Inside Pitches"
                required
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Category *</label>
              <select
                style={styles.select}
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                required
              >
                {categories.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat.charAt(0).toUpperCase() + cat.slice(1)}
                  </option>
                ))}
              </select>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Difficulty Level *</label>
              <select
                style={styles.select}
                value={formData.difficulty_level}
                onChange={(e) => setFormData({ ...formData, difficulty_level: e.target.value })}
                required
              >
                {difficulties.map((diff) => (
                  <option key={diff} value={diff}>
                    {diff.charAt(0).toUpperCase() + diff.slice(1)}
                  </option>
                ))}
              </select>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Duration (minutes)</label>
              <input
                type="number"
                style={styles.input}
                value={formData.duration_minutes || ''}
                onChange={(e) =>
                  setFormData({ ...formData, duration_minutes: parseInt(e.target.value) || null })
                }
                placeholder="e.g., 15"
                min="1"
              />
            </div>

            <div style={{ ...styles.formGroup, ...styles.formGroupFull }}>
              <label style={styles.label}>Description</label>
              <textarea
                style={styles.textarea}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Describe the drill, its purpose, and key coaching points..."
                rows="4"
              />
            </div>

            {/* Video Section */}
            <div style={{ ...styles.formGroup, ...styles.formGroupFull }}>
              <div style={styles.mediaSection}>
                <label style={styles.sectionLabel}>Video</label>

                {/* Source Toggle */}
                <div style={styles.sourceToggle}>
                  <button
                    type="button"
                    style={{
                      ...styles.toggleButton,
                      ...(videoSource === 'url' ? styles.toggleButtonActive : {}),
                    }}
                    onClick={() => setVideoSource('url')}
                  >
                    URL
                  </button>
                  <button
                    type="button"
                    style={{
                      ...styles.toggleButton,
                      ...(videoSource === 'upload' ? styles.toggleButtonActive : {}),
                    }}
                    onClick={() => setVideoSource('upload')}
                  >
                    Upload
                  </button>
                </div>

                {/* URL Input or Upload Field */}
                {videoSource === 'url' ? (
                  <input
                    type="url"
                    style={styles.input}
                    value={formData.video_url}
                    onChange={(e) => setFormData({ ...formData, video_url: e.target.value })}
                    placeholder="https://youtube.com/... or https://vimeo.com/..."
                  />
                ) : (
                  <MediaUploadField
                    label=""
                    accept="video/mp4,video/quicktime,video/webm,video/x-msvideo"
                    maxSize={524288000} // 500MB
                    currentUrl={videoStoragePath ? 'Uploaded video file' : null}
                    onUpload={handleVideoUpload}
                    onRemove={handleVideoRemove}
                    type="video"
                    disabled={mediaUploading}
                  />
                )}
              </div>
            </div>

            {/* Thumbnail auto-extracted message */}
            {videoSource === 'upload' && extractingThumbnail && (
              <div style={{ ...styles.formGroup, ...styles.formGroupFull }}>
                <div
                  style={{
                    padding: '12px',
                    backgroundColor: '#e3f2fd',
                    borderRadius: '4px',
                    color: '#1565c0',
                    fontSize: '14px',
                  }}
                >
                  Extracting thumbnail from video...
                </div>
              </div>
            )}
            {videoSource === 'upload' && !extractingThumbnail && thumbnailFile && (
              <div style={{ ...styles.formGroup, ...styles.formGroupFull }}>
                <div
                  style={{
                    padding: '12px',
                    backgroundColor: '#e8f5e9',
                    borderRadius: '4px',
                    color: '#2e7d32',
                    fontSize: '14px',
                  }}
                >
                  ✓ Thumbnail automatically extracted from video
                </div>
              </div>
            )}

            <div style={{ ...styles.formGroup, ...styles.formGroupFull }}>
              <label style={styles.label}>Equipment Needed</label>
              <div style={styles.equipmentList}>
                {formData.equipment_needed.map((equipment, index) => (
                  <div key={index} style={styles.equipmentItem}>
                    <input
                      type="text"
                      style={{ ...styles.input, flex: 1 }}
                      value={equipment}
                      onChange={(e) => handleEquipmentChange(index, e.target.value)}
                      placeholder="e.g., Bat, Tee, Balls"
                    />
                    <button
                      type="button"
                      style={styles.removeButton}
                      onClick={() => removeEquipmentField(index)}
                    >
                      Remove
                    </button>
                  </div>
                ))}
                <button type="button" style={styles.addButton} onClick={addEquipmentField}>
                  + Add Equipment
                </button>
              </div>
            </div>

            {/* Media upload error */}
            {mediaError && (
              <div style={{ ...styles.formGroupFull }}>
                <div style={styles.error}>{mediaError}</div>
              </div>
            )}

            <div style={styles.formActions}>
              <button
                type="button"
                style={{ ...styles.button, ...styles.secondaryButton }}
                onClick={handleCancel}
                disabled={mediaUploading}
              >
                Cancel
              </button>
              <button
                type="submit"
                style={{
                  ...styles.button,
                  ...styles.primaryButton,
                  opacity: mediaUploading ? 0.6 : 1,
                  cursor: mediaUploading ? 'not-allowed' : 'pointer',
                }}
                disabled={mediaUploading}
              >
                {mediaUploading ? 'Uploading...' : editingDrill ? 'Update Drill' : 'Create Drill'}
              </button>
            </div>
          </form>
        </div>
      ) : (
        <>
          <div style={styles.card}>
            <div style={styles.cardHeader}>
              <h2 style={styles.cardTitle}>Drill Library ({filteredDrills.length})</h2>
              <button style={{ ...styles.button, ...styles.primaryButton }} onClick={handleCreate}>
                + Create Drill
              </button>
            </div>

            <div style={styles.filters}>
              <input
                type="text"
                style={styles.filterInput}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search drills..."
              />
              <select
                style={styles.select}
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
              >
                <option value="">All Categories</option>
                {categories.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat.charAt(0).toUpperCase() + cat.slice(1)}
                  </option>
                ))}
              </select>
              <select
                style={styles.select}
                value={difficultyFilter}
                onChange={(e) => setDifficultyFilter(e.target.value)}
              >
                <option value="">All Difficulties</option>
                {difficulties.map((diff) => (
                  <option key={diff} value={diff}>
                    {diff.charAt(0).toUpperCase() + diff.slice(1)}
                  </option>
                ))}
              </select>
            </div>

            {filteredDrills.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                {searchTerm || categoryFilter || difficultyFilter ? (
                  <p>No drills match your filters.</p>
                ) : (
                  <p>No drills yet. Create your first drill to get started!</p>
                )}
              </div>
            ) : isDesktop ? (
              <div style={styles.tableContainer}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>Name</th>
                      <th style={styles.th}>Category</th>
                      <th style={styles.th}>Difficulty</th>
                      <th style={styles.th}>Duration</th>
                      <th style={styles.th}>Equipment</th>
                      <th style={styles.th}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredDrills.map((drill) => (
                      <tr key={drill.id}>
                        <td style={styles.td}>
                          <strong>{drill.name}</strong>
                          {drill.description && (
                            <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                              {drill.description.substring(0, 60)}
                              {drill.description.length > 60 && '...'}
                            </div>
                          )}
                        </td>
                        <td style={styles.td}>
                          <span style={getCategoryBadgeStyle(drill.category)}>
                            {drill.category}
                          </span>
                        </td>
                        <td style={styles.td}>
                          <span style={getDifficultyBadgeStyle(drill.difficulty_level)}>
                            {drill.difficulty_level}
                          </span>
                        </td>
                        <td style={styles.td}>
                          {drill.duration_minutes ? `${drill.duration_minutes} min` : 'N/A'}
                        </td>
                        <td style={styles.td}>
                          {drill.equipment_needed && drill.equipment_needed.length > 0
                            ? drill.equipment_needed.join(', ')
                            : 'None'}
                        </td>
                        <td style={styles.td}>
                          <div style={styles.actionButtons}>
                            <button
                              style={{
                                ...styles.smallButton,
                                backgroundColor: '#007bff',
                                color: 'white',
                              }}
                              onClick={() => handleEdit(drill)}
                            >
                              Edit
                            </button>
                            <button
                              style={{
                                ...styles.smallButton,
                                backgroundColor: '#dc3545',
                                color: 'white',
                              }}
                              onClick={() => handleDelete(drill)}
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div>
                {filteredDrills.map((drill) => (
                  <div key={drill.id} style={styles.drillCard}>
                    <div style={styles.drillCardHeader}>
                      <div style={{ flex: 1 }}>
                        <h3 style={styles.drillCardTitle}>{drill.name}</h3>
                        <div style={styles.drillCardMeta}>
                          <span style={getCategoryBadgeStyle(drill.category)}>
                            {drill.category}
                          </span>
                          <span style={getDifficultyBadgeStyle(drill.difficulty_level)}>
                            {drill.difficulty_level}
                          </span>
                          {drill.duration_minutes && (
                            <span style={styles.badge}>{drill.duration_minutes} min</span>
                          )}
                        </div>
                      </div>
                    </div>
                    {drill.description && (
                      <p style={{ fontSize: '14px', color: '#666', marginBottom: '12px' }}>
                        {drill.description}
                      </p>
                    )}
                    {drill.equipment_needed && drill.equipment_needed.length > 0 && (
                      <p style={{ fontSize: '12px', color: '#999', marginBottom: '12px' }}>
                        <strong>Equipment:</strong> {drill.equipment_needed.join(', ')}
                      </p>
                    )}
                    <div style={styles.actionButtons}>
                      <button
                        style={{
                          ...styles.smallButton,
                          backgroundColor: '#007bff',
                          color: 'white',
                        }}
                        onClick={() => handleEdit(drill)}
                      >
                        Edit
                      </button>
                      <button
                        style={{
                          ...styles.smallButton,
                          backgroundColor: '#dc3545',
                          color: 'white',
                        }}
                        onClick={() => handleDelete(drill)}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default DrillLibraryManager;
